(() => {
  'use strict';
  if (window.TumugiPreorder) return;
  const parse = (element, fallback) => {
    try { return JSON.parse(element?.textContent || 'null') ?? fallback; } catch (_) { return fallback; }
  };
  const config = parse(document.getElementById('tumugi-preorder-config'), {});
  const handles = String(config.handles || '').split(',').map(value => value.trim()).filter(Boolean);
  const period = config.period || '2026年12月下旬';
  const rootPath = window.Shopify?.routes?.root || '/';
  const shopRoot = rootPath.endsWith('/') ? rootPath : rootPath + '/';
  const eligible = handle => Boolean(config.enabled && handles.includes(handle));
  // Use existing warehouse inventory and Shopify's native continue-selling policy.
  // No reservation location, variant metafield, or artificial inventory is needed.
  const remaining = variant => variant?.inventory_management ? Math.max(0, Number(variant.inventory_quantity) || 0) : Infinity;
  const stock = remaining;
  const reservable = (variant, handle, preview = false) => Boolean(variant && eligible(handle) && variant.inventory_management && variant.requires_shipping !== false &&
    (variant.inventory_policy === 'continue' || preview && config.editorPreview));
  const needsReservation = (variant, quantity, handle) => Boolean(reservable(variant, handle, true) && Number(quantity) > stock(variant));
  const saleLimit = (variant, handle, preview = false) => {
    if (!variant) return 0;
    if (preview && config.editorPreview && eligible(handle) && variant.inventory_management && variant.requires_shipping !== false) return Infinity;
    if (!variant.available) return 0;
    return !variant.inventory_management || variant.inventory_policy === 'continue' ? Infinity : remaining(variant);
  };
  const orderText = () => period + 'に全商品まとめて発送予定';
  const propertyNames = ['発送予定', '予約対象', '_tumugi_preorder', '_tumugi_preorder_order', '_tumugi_preorder_period', '_tumugi_preorder_variants', '_tumugi_dispatch_managed'];
  const cleanProperties = input => {
    const properties = { ...(input || {}) };
    if (properties._tumugi_dispatch_managed === '1') propertyNames.forEach(key => delete properties[key]);
    return properties;
  };
  const equal = (left, right) => JSON.stringify(Object.entries(left || {}).sort()) === JSON.stringify(Object.entries(right || {}).sort());
  const setText = (root, selector, value) => { const node = root.querySelector(selector); if (node) node.textContent = value; };

  const analyze = snapshot => {
    const items = snapshot?.items || [];
    const quantities = new Map();
    items.forEach(item => quantities.set(String(item.id), (quantities.get(String(item.id)) || 0) + Number(item.quantity)));
    const reserved = items.filter(item => needsReservation(item, quantities.get(String(item.id)), item.handle));
    // Preserve an existing reservation when reservation sales are subsequently switched off.
    if (!config.enabled) {
      const ids = new Set(items.flatMap(item => String(item.properties?._tumugi_preorder_variants || '').split(',')));
      items.forEach(item => {
        if ((item.properties?._tumugi_preorder === '1' || ids.has(String(item.id))) && !reserved.includes(item)) reserved.push(item);
      });
    }
    return { hasPreorder: reserved.length > 0, reservedIds: new Set(reserved.map(item => String(item.id))), period,
      signature: reserved.length ? 'reservation:' + period : 'normal' };
  };

  let latestSnapshot = null;
  let snapshotRequest = null;
  const fetchSnapshot = (force = false) => {
    if (snapshotRequest) return force ? snapshotRequest.catch(() => {}).then(() => fetchSnapshot()) : snapshotRequest;
    snapshotRequest = fetch(shopRoot + 'cart?view=tumugi-preorder&_=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' })
      .then(response => { if (!response.ok) throw new Error('cart'); return response.json(); })
      .then(snapshot => { if (!Array.isArray(snapshot.items)) throw new Error('cart'); latestSnapshot = snapshot; return snapshot; })
      .finally(() => { snapshotRequest = null; });
    return snapshotRequest;
  };
  const renderCarts = snapshot => {
    latestSnapshot = snapshot;
    const state = analyze(snapshot);
    document.querySelectorAll('tumugi-cart-dispatch').forEach(element => {
      element.dataset.signature = state.signature;
      element.dataset.hasPreorder = String(state.hasPreorder);
      const box = element.querySelector('[data-cart-dispatch-box]');
      if (box) { box.hidden = !state.hasPreorder; box.classList.toggle('tumugi-dispatch--reservation', state.hasPreorder); }
      setText(element, '[data-cart-dispatch-title]', orderText());
    });
    // Wallet buttons bypass the cart submit event. Keep the standard checkout for reservations.
    const recordedReservation = snapshot.items.some(item => item.properties?._tumugi_dispatch_managed === '1') || snapshot.attributes?._tumugi_preorder === '1';
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach(element => { element.hidden = state.hasPreorder || recordedReservation; });
    return state;
  };

  class CartDispatch extends HTMLElement {
    connectedCallback() {
      const snapshot = parse(this.querySelector('[data-preorder-cart]'), null);
      if (snapshot) renderCarts(snapshot);
    }
  }
  if (!customElements.get('tumugi-cart-dispatch')) customElements.define('tumugi-cart-dispatch', CartDispatch);

  class ProductDispatch extends HTMLElement {
    connectedCallback() {
      this.variants = parse(this.querySelector('[data-preorder-variants]'), []);
      this.productInfo = this.closest('product-info') || this.parentElement;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.onChange = event => {
        // Variant options are fetched asynchronously by Dawn. Wait for its id/variant event.
        if (event.target.name === 'id' || event.target.name === 'quantity') queueMicrotask(() => this.update());
      };
      this.productInfo.addEventListener('change', this.onChange);
      this.productInfo.addEventListener('input', this.onChange);
      this.observer = new MutationObserver(() => this.decorateOptions());
      const picker = this.productInfo.querySelector('variant-selects');
      if (picker) this.observer.observe(picker, { childList: true });
      this.update();
      if (typeof subscribe === 'function') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) === String(this.productInfo.dataset.section)) this.update();
      });
    }
    disconnectedCallback() {
      this.productInfo?.removeEventListener('change', this.onChange);
      this.productInfo?.removeEventListener('input', this.onChange);
      this.observer?.disconnect();
      this.unsubscribe?.();
    }
    selected() {
      return this.variants.find(variant => String(variant.id) === this.form.querySelector('[name="id"]')?.value);
    }
    quantity() { return Math.max(1, Number(this.productInfo.querySelector('[name="quantity"]')?.value) || 1); }
    update() {
      const variant = this.selected();
      const quantity = this.quantity();
      const reservation = needsReservation(variant, quantity, this.dataset.productHandle);
      const canReserve = reservable(variant, this.dataset.productHandle, true);
      const limit = saleLimit(variant, this.dataset.productHandle, true);
      const soldOut = Boolean(variant && (!limit || quantity > limit));
      const box = this.querySelector('[data-product-dispatch]');
      box.classList.toggle('tumugi-dispatch--reservation', reservation);
      this.dataset.reservation = String(reservation);
      setText(this, '[data-dispatch-status]', soldOut ? '在庫切れ' : reservation ? '予約販売' : '通常発送');
      setText(this, '[data-dispatch-title]', !variant ? 'サイズ・カラーを選択してください' : soldOut ? '選択枚数分の在庫がありません' : reservation ? period + '発送予定' : '最短翌営業日発送');
      setText(this, '[data-dispatch-body]', reservation ? 'この商品を含むご注文は、在庫のある商品もまとめて上記の時期に発送します。' : 'ご決済確認後に発送します。予約商品を同時に購入する場合は、予約商品とまとめて発送します。');
      const editorOnly = reservation && config.editorPreview && !reservable(variant, this.dataset.productHandle, false);
      this.querySelector('[data-dispatch-editor]').hidden = !editorOnly;
      this.productInfo.querySelectorAll('[data-tumugi-dispatch-pill]').forEach(element => {
        element.textContent = reservation ? period + '発送予定' : '最短翌営業日発送';
      });
      const submit = this.form.querySelector('[name="add"]');
      if (submit && variant) {
        if ((!limit || quantity > limit) && !submit.classList.contains('loading')) {
          submit.disabled = true;
          submit.querySelector('span').textContent = '選択枚数分の在庫がありません';
        } else if (reservation && canReserve && !submit.classList.contains('loading')) {
          submit.disabled = false;
          submit.querySelector('span').textContent = '予約商品をカートに追加する';
        } else if (variant.available && !submit.classList.contains('loading')) {
          submit.disabled = false;
          submit.querySelector('span').textContent = window.variantStrings?.addToCart || 'カートに追加する';
        }
      }
      const dynamicCheckout = this.form.querySelector('.shopify-payment-button');
      if (dynamicCheckout) dynamicCheckout.hidden = reservation;
      // These are public line item properties: visible in checkout and on the order.
      this.form.querySelectorAll('[data-tumugi-property]').forEach(input => input.remove());
      if (reservation && canReserve) {
        const properties = { '発送予定': orderText(), '_tumugi_preorder': '1', '_tumugi_preorder_period': period, '_tumugi_dispatch_managed': '1' };
        Object.entries(properties).forEach(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden'; input.name = 'properties[' + name + ']'; input.value = value; input.dataset.tumugiProperty = '';
          this.form.appendChild(input);
        });
      }
      this.decorateOptions();
      this.alternatives(variant, quantity, reservation);
    }
    decorateOptions() {
      const selected = this.selected();
      if (!selected) return;
      const groups = this.productInfo.querySelectorAll('variant-selects > fieldset, variant-selects > .product-form__input--dropdown');
      groups.forEach((group, index) => {
        const choices = group.querySelectorAll('input[type="radio"], select option');
        choices.forEach(choice => {
          const variant = this.variants.find(candidate => candidate.options.every((value, position) => value === (position === index ? choice.value : selected.options[position])));
          const reservation = needsReservation(variant, 1, this.dataset.productHandle) && reservable(variant, this.dataset.productHandle, true);
          if (choice.tagName === 'OPTION') {
            if (!choice.dataset.tumugiLabel) choice.dataset.tumugiLabel = choice.textContent.trim();
            choice.textContent = choice.dataset.tumugiLabel + (reservation ? '（予約・' + period + '発送）' : '');
          } else {
            const label = group.querySelector('label[for="' + CSS.escape(choice.id) + '"]');
            if (!label) return;
            label.querySelector('.tumugi-preorder-badge')?.remove();
            label.title = reservation ? period + '発送予定' : '';
            if (reservation) {
              const badge = document.createElement('span'); badge.className = 'tumugi-preorder-badge'; badge.textContent = '予約';
              label.appendChild(badge);
              if (config.editorPreview) choice.classList.remove('disabled');
            }
          }
        });
      });
    }
    alternatives(variant, quantity, reservation) {
      const container = this.querySelector('[data-dispatch-alternatives]');
      container.replaceChildren(); container.hidden = true;
      if (!reservation || !variant) return;
      // Keep every non-colour option (in particular size) unchanged.
      const picker = this.productInfo.querySelector('variant-selects');
      const groups = [...(picker?.querySelectorAll(':scope > fieldset, :scope > .product-form__input--dropdown') || [])];
      const colorIndex = groups.findIndex(group => /色|カラー|colou?r/i.test(group.querySelector('legend, label')?.textContent || ''));
      if (colorIndex < 0) return;
      const alternatives = this.variants.filter(candidate => candidate.available && stock(candidate) >= quantity && candidate.options.every((value, index) => index === colorIndex || value === variant.options[index]));
      if (!alternatives.length) return;
      const label = document.createElement('div'); label.textContent = '同じサイズですぐ発送できるカラー'; container.appendChild(label);
      alternatives.forEach(candidate => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'tumugi-dispatch__alternative'; button.textContent = candidate.options[colorIndex];
        button.addEventListener('click', () => {
          const group = groups[colorIndex];
          const radio = [...group.querySelectorAll('input[type="radio"]')].find(input => input.value === candidate.options[colorIndex]);
          if (radio) radio.click();
          else { const select = group.querySelector('select'); if (select) { select.value = candidate.options[colorIndex]; select.dispatchEvent(new Event('change', { bubbles: true })); } }
        });
        container.appendChild(button);
      });
      container.hidden = false;
    }
  }
  if (!customElements.get('tumugi-preorder-product')) customElements.define('tumugi-preorder-product', ProductDispatch);

  const prepareProduct = productForm => {
    const component = productForm.querySelector('tumugi-preorder-product');
    if (!component) return true;
    component.update();
    const variant = component.selected();
    if (component.quantity() > saleLimit(variant, component.dataset.productHandle, false)) {
      productForm.handleErrorMessage(config.editorPreview && component.dataset.reservation === 'true' ? '予約表示のプレビューです。実際の購入には対象バリエーションの「在庫切れの場合でも販売を続ける」をONにしてください。' : '選択枚数分の在庫がありません。枚数を減らすか、別のカラーをご確認ください。');
      return false;
    }
    if (component.dataset.reservation === 'true' && variant?.available === false) {
      productForm.handleErrorMessage(config.editorPreview ? '予約表示のプレビューです。実際の購入には対象バリエーションの「在庫切れの場合でも販売を続ける」をONにしてください。' : 'この商品は現在購入できません。別のカラーをご確認ください。');
      return false;
    }
    return true;
  };

  const normalizeCart = async snapshot => {
    if (!analyze(snapshot).hasPreorder && !snapshot.items.some(item => item.properties?._tumugi_dispatch_managed === '1') && !snapshot.attributes?._tumugi_preorder) return;
    const inventory = new Map(snapshot.items.map(item => [String(item.id), item]));
    let current = snapshot;
    // Changing properties can merge identical lines. Rebuild from each response rather than using stale line keys.
    for (let attempt = 0; attempt < snapshot.items.length * 2 + 2; attempt += 1) {
      const state = analyze(current);
      const pending = current.items.map((item, index) => {
        const properties = cleanProperties(item.properties);
        if (state.hasPreorder) {
          Object.assign(properties, { '発送予定': orderText(), '_tumugi_preorder_period': period, '_tumugi_dispatch_managed': '1' });
          if (state.reservedIds.has(String(item.id))) properties._tumugi_preorder = '1';
          if (index === 0) {
            properties._tumugi_preorder_order = '1';
            properties._tumugi_preorder_variants = [...state.reservedIds].join(',');
          }
        }
        return { item, properties };
      }).find(({ item, properties }) => !equal(item.properties, properties));
      if (!pending) {
        const response = await fetch(shopRoot + 'cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: {
          '発送予定': state.hasPreorder ? orderText() : '', '配送方法': state.hasPreorder ? '予約商品と全商品まとめて発送' : '', '_tumugi_preorder': state.hasPreorder ? '1' : ''
        } }) });
        if (!response.ok) throw new Error('cart');
        return;
      }
      const response = await fetch(shopRoot + 'cart/change.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: pending.item.key, quantity: pending.item.quantity, properties: pending.properties }) });
      if (!response.ok) throw new Error('cart');
      const cart = await response.json();
      current = { ...snapshot, items: cart.items.map(item => ({ ...inventory.get(String(item.variant_id)), ...item, id: item.variant_id, handle: item.handle || inventory.get(String(item.variant_id))?.handle })) };
    }
    throw new Error('cart');
  };

  let checkingOut = false;
  const checkout = async (event, button) => {
    if (!config.enabled && !latestSnapshot?.items.some(item => item.properties?._tumugi_dispatch_managed === '1')) return;
    event.preventDefault();
    if (checkingOut) return;
    checkingOut = true;
    const text = button.textContent; button.disabled = true; button.textContent = '発送予定を確認中…';
    const previous = latestSnapshot ? analyze(latestSnapshot).signature : null;
    try {
      const snapshot = await fetchSnapshot(true);
      const state = renderCarts(snapshot);
      if (state.hasPreorder && previous !== state.signature) {
        const box = document.querySelector('[data-cart-dispatch-box]');
        box?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (box) { box.setAttribute('tabindex', '-1'); box.focus({ preventScroll: true }); }
        throw new Error('changed');
      }
      await normalizeCart(snapshot);
      window.location.assign(shopRoot + 'checkout');
    } catch (error) {
      let target = button.closest('cart-notification, cart-drawer, .cart__blocks')?.querySelector('[data-cart-dispatch-error]') || document.querySelector('[data-cart-dispatch-error]');
      if (!target) { target = document.createElement('p'); target.setAttribute('role', 'alert'); target.className = 'tumugi-dispatch__body'; button.insertAdjacentElement('beforebegin', target); }
      target.hidden = false;
      target.textContent = error.message === 'changed' ? '発送予定をご確認のうえ、もう一度購入手続きへお進みください。' : '発送予定を確認できませんでした。少し時間をおいて、もう一度お試しください。';
      checkingOut = false; button.disabled = false; button.textContent = text;
    }
  };

  window.TumugiPreorder = { config, period, eligible, stock, remaining, saleLimit, reservable, needsReservation, orderText, analyze, prepareProduct,
    // Cart permalinks support public properties on the first line. Preserve the LP's existing cart and discount flow.
    permalinkParams(params, entries, hasPreorder) {
      if (!hasPreorder) return;
      const ids = [...new Set(entries.filter(entry => entry.preorder).map(entry => String(entry.variant.id)))];
      const targets = [...new Set(entries.filter(entry => entry.preorder).map(entry => (entry.noun ? entry.noun + ' ' : '') + (entry.variant.public_title || entry.variant.title || entry.color || '')).filter(Boolean))].join('・');
      const properties = { '発送予定': this.orderText(), '予約対象': targets, '_tumugi_preorder_order': '1', '_tumugi_preorder_variants': ids.join(','), '_tumugi_preorder_period': period, '_tumugi_dispatch_managed': '1' };
      const bytes = new TextEncoder().encode(JSON.stringify(properties));
      params.set('properties', btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join('')).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''));
      params.set('attributes[発送予定]', this.orderText());
      params.set('attributes[配送方法]', '予約商品と全商品まとめて発送');
      params.set('attributes[_tumugi_preorder]', '1');
    }
  };
  document.addEventListener('submit', event => {
    const button = event.submitter || event.target.querySelector('[name="checkout"]');
    if (button?.name === 'checkout') checkout(event, button);
  }, true);
  const initialize = () => {
    document.querySelectorAll('tumugi-cart-dispatch [data-preorder-cart]').forEach(element => renderCarts(parse(element, { items: [] })));
    if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, () => fetchSnapshot().then(renderCarts).catch(() => {}));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true }); else initialize();
})();
